import { useState, useEffect } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const initialState = {
  name: "",
  company: "",
  phone: "",
  email: "",
  request_type: "comprar",
  quantity: "",
  message: "",
};

export default function QuoteForm({ preselected, onConsumePreselect }) {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preselected) {
      setForm((f) => ({
        ...f,
        request_type: "comprar",
        message: `Modelo de interés: ${preselected.name} (${preselected.dim}). ${
          f.message ? "\n" + f.message : ""
        }`.trim(),
      }));
      if (onConsumePreselect) onConsumePreselect();
    }
  }, [preselected, onConsumePreselect]);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const payload = { ...form, email: form.email.trim() || undefined };
      await api.post("/quotes", payload);
      toast.success("Solicitud enviada", {
        description: "Te contactaremos en menos de una hora hábil.",
      });
      setForm(initialState);
    } catch (err) {
      toast.error("No se pudo enviar", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="cotizar"
      data-testid="quote-section"
      className="bg-vp-navy-deep text-white border-b-2 border-vp-yellow"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-5 px-6 lg:px-12 py-14 lg:py-20 border-b-2 lg:border-b-0 lg:border-r-2 border-white/15 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-[2px] bg-vp-yellow" />
              <span className="label-industrial">05 · Cotización rápida</span>
            </div>
            <h2 className="heading-industrial text-4xl sm:text-5xl lg:text-6xl mb-6 text-white">
              5 campos.
              <br />
              <span className="text-vp-yellow">1 hora.</span>
              <br />
              Cero rodeos.
            </h2>
            <p className="text-white/80 max-w-md text-lg">
              Los jefes de operaciones no tienen tiempo. Por eso este
              formulario solo pide lo indispensable para preparar una
              cotización seria.
            </p>
          </div>

          <div className="mt-12 pt-8 border-t border-white/15">
            <div className="label-industrial mb-2">Cómo seguimos</div>
            <ol className="space-y-2 text-sm text-white/80">
              <li>
                <span className="text-vp-yellow font-bold">01 →</span>{" "}
                Recibimos tu solicitud.
              </li>
              <li>
                <span className="text-vp-yellow font-bold">02 →</span>{" "}
                Te llamamos para validar volumen y plazos.
              </li>
              <li>
                <span className="text-vp-yellow font-bold">03 →</span>{" "}
                Enviamos cotización formal por correo.
              </li>
            </ol>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="quote-form"
          className="lg:col-span-7 bg-vp-navy text-white px-6 lg:px-12 py-12 lg:py-16"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-2 border-vp-yellow">
            <Field
              label="Nombre"
              testid="quote-name"
              value={form.name}
              onChange={setField("name")}
              required
              placeholder="María González"
            />
            <Field
              label="Empresa"
              testid="quote-company"
              value={form.company}
              onChange={setField("company")}
              required
              placeholder="Logística Andes S.A."
              border="left"
            />
            <Field
              label="Teléfono"
              testid="quote-phone"
              value={form.phone}
              onChange={setField("phone")}
              required
              type="tel"
              placeholder="+56 9 1234 5678"
              border="top"
            />
            <Field
              label="Correo (opcional)"
              testid="quote-email"
              value={form.email}
              onChange={setField("email")}
              type="email"
              placeholder="operaciones@empresa.cl"
              border="top-left"
            />

            <div className="sm:col-span-2 border-t-2 border-vp-yellow p-6">
              <div className="label-industrial mb-3">¿Qué necesitas?</div>
              <div className="grid grid-cols-2 border-2 border-vp-yellow">
                {[
                  { v: "comprar", l: "Comprar pallets" },
                  { v: "vender", l: "Vender mis pallets" },
                ].map((opt, i) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`quote-type-${opt.v}`}
                    onClick={() => setForm({ ...form, request_type: opt.v })}
                    className={`py-4 font-bold uppercase tracking-widest text-sm transition-colors ${
                      i === 0 ? "border-r-2 border-vp-yellow" : ""
                    } ${
                      form.request_type === opt.v
                        ? "bg-vp-yellow text-[#001E62]"
                        : "bg-vp-navy text-white hover:bg-vp-navy-soft"
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <Field
              label="Cantidad aproximada"
              testid="quote-quantity"
              value={form.quantity}
              onChange={setField("quantity")}
              required
              placeholder="200 unidades / 1 camión / etc."
              border="top"
              fullWidth
            />

            <div className="sm:col-span-2 border-t-2 border-vp-yellow p-6">
              <label className="label-industrial mb-2 block">
                Mensaje (opcional)
              </label>
              <textarea
                data-testid="quote-message"
                value={form.message}
                onChange={setField("message")}
                rows={3}
                placeholder="Cuéntanos plazos, medidas específicas o cualquier detalle relevante."
                className="w-full bg-vp-navy-deep border-2 border-white/20 p-3 text-white placeholder-white/40 focus:border-vp-yellow resize-none"
                maxLength={1000}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="quote-submit"
            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-vp-red hover:bg-vp-yellow hover:text-[#001E62] disabled:opacity-60 text-white font-bold uppercase tracking-widest text-sm px-10 py-5 transition-colors"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <>
                Enviar solicitud de cotización
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  placeholder,
  type = "text",
  border,
  fullWidth,
  testid,
}) {
  const borderClasses = {
    left: "sm:border-l-2 border-vp-yellow",
    top: "border-t-2 border-vp-yellow",
    "top-left": "border-t-2 sm:border-l-2 border-vp-yellow",
    undefined: "",
  };
  return (
    <div
      className={`p-6 ${borderClasses[border] || ""} ${
        fullWidth ? "sm:col-span-2" : ""
      }`}
    >
      <label className="label-industrial mb-2 block">
        {label}
        {required && <span className="text-vp-red ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-vp-navy-deep border-2 border-white/20 px-3 py-2.5 text-white placeholder-white/40 focus:border-vp-yellow"
      />
    </div>
  );
}
